'use client';

// useGoogleExtraction - TanStack Query mutation for Google Sheets/Docs content extraction
// Story 6.5: Added referrerBotId for viral tracking attribution

import { useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { saveSessionToStorage, getOrCreateClientSession } from '../lib/session';
import { getReferral, clearReferral } from '../lib/referralTracking';
import type { GoogleDocContent, GoogleDocStatus } from '../features/content-input/GoogleDocPreviewCard';
import type {
  ExtractGoogleRequest,
  ExtractGoogleResponse,
  ExtractGoogleErrorResponse,
  GoogleExtractionErrorCode,
} from '../lib/google-extraction';

/**
 * Ensures session is synced before extraction
 */
async function ensureSessionSync(): Promise<void> {
  try {
    const localSession = getOrCreateClientSession();

    const response = await fetch('/api/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId: localSession }),
    });

    const data = await response.json();
    if (data.data?.sessionId) {
      saveSessionToStorage(data.data.sessionId);
    }
  } catch {
    console.warn('[session] Pre-extraction session sync failed');
  }
}

/**
 * Options for Google extraction request with referral support
 */
interface ExtractGoogleRequestWithReferral extends ExtractGoogleRequest {
  /** Optional referrer bot ID for viral tracking attribution (Story 6.5) */
  referrerBotId?: string;
}

/**
 * Extracts content from a Google document via the API
 */
async function extractGoogleDocument(request: ExtractGoogleRequestWithReferral): Promise<ExtractGoogleResponse> {
  await ensureSessionSync();

  // Story 6.5: Add referral attribution for new bot creation
  const isNewBot = !request.botId;
  const storedReferral = getReferral();
  const referrerBotId = request.referrerBotId || storedReferral || undefined;

  const response = await fetch('/api/bots/extract-google', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...request,
      // Only include referrerBotId for new bot creation
      ...(isNewBot && referrerBotId ? { referrerBotId } : {}),
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw data as ExtractGoogleErrorResponse;
  }

  // Story 6.5: Clear referral after successful bot creation
  if (isNewBot && referrerBotId) {
    clearReferral();
  }

  return data as ExtractGoogleResponse;
}

/**
 * Generates a unique ID for tracking extracted content
 */
function generateContentId(): string {
  return `google-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

export interface UseGoogleExtractionOptions {
  /** Bot ID to associate extractions with */
  botId?: string;
  /** Callback when extraction completes successfully */
  onSuccess?: (content: GoogleDocContent) => void;
  /** Callback when extraction fails */
  onError?: (content: GoogleDocContent, error: ExtractGoogleErrorResponse) => void;
}

export interface UseGoogleExtractionReturn {
  /** List of extracted Google documents with their status */
  contents: GoogleDocContent[];
  /** Whether any extraction is in progress */
  isExtracting: boolean;
  /** Extract content from a Google Sheets or Docs URL */
  extractGoogleDoc: (params: { url: string; botId?: string }) => void;
  /** Remove content from the list by local ID */
  removeContent: (contentId: string) => void;
  /** Remove content from the list by database contentId */
  removeByContentId: (contentId: string) => void;
  /** Clear all contents */
  clearContents: () => void;
  /** Total character count across all extracted content */
  totalCharacterCount: number;
  /** Bot ID created for extractions (shared across all content) */
  botId: string | null;
}

/**
 * Hook for managing Google Sheets/Docs content extraction with TanStack Query
 *
 * Features:
 * - Google document extraction via API
 * - Automatic document type detection (Sheets vs Docs)
 * - Progress tracking per document
 * - Error handling with i18n support
 * - Optimistic updates for better UX
 *
 * @example
 * ```tsx
 * const { contents, isExtracting, extractGoogleDoc, removeContent } = useGoogleExtraction({
 *   onSuccess: (content) => console.log('Extracted:', content.title),
 * });
 *
 * return (
 *   <>
 *     <UrlInputField onSubmit={(url) => extractGoogleDoc({ url })} isLoading={isExtracting} />
 *     <GoogleDocPreviewList contents={contents} onRemove={removeContent} />
 *   </>
 * );
 * ```
 */
export function useGoogleExtraction(options?: UseGoogleExtractionOptions): UseGoogleExtractionReturn {
  const queryClient = useQueryClient();
  const [contents, setContents] = useState<GoogleDocContent[]>([]);
  const [createdBotId, setCreatedBotId] = useState<string | null>(options?.botId ?? null);

  // Google document extraction mutation
  const extractMutation = useMutation({
    mutationFn: ({ url, contentId, botId }: { url: string; contentId: string; botId?: string }) => {
      const effectiveBotId = botId ?? createdBotId ?? options?.botId;
      return extractGoogleDocument({
        url,
        ...(effectiveBotId ? { botId: effectiveBotId } : {}),
      });
    },
    onMutate: async ({ url, contentId }) => {
      // Determine document type from URL
      const sourceType = url.includes('/spreadsheets/') ? 'google_sheets' : 'google_docs';

      // Optimistic update - add content with extracting status
      const newContent: GoogleDocContent = {
        id: contentId,
        title: sourceType === 'google_sheets' ? 'Google Sheets' : 'Google Docs',
        url,
        sourceType,
        status: 'extracting' as GoogleDocStatus,
      };

      setContents((prev) => [...prev, newContent]);
    },
    onSuccess: (response, { contentId }) => {
      const { botId: responseBotId, contentId: dbContentId, title, excerpt, characterCount, sourceType, sourceUrl } = response.data;

      // Track the bot ID from the API response
      if (responseBotId && !createdBotId) {
        setCreatedBotId(responseBotId);
      }

      // Build the content result
      const successContent: GoogleDocContent = {
        id: contentId,
        contentId: dbContentId,
        title,
        url: sourceUrl,
        excerpt,
        characterCount,
        sourceType,
        status: 'ready' as GoogleDocStatus,
      };

      // Update contents state
      setContents((prev) => prev.map((c) => (c.id === contentId ? successContent : c)));

      // Invalidate content sources query to refresh the unified list
      if (responseBotId) {
        queryClient.invalidateQueries({ queryKey: ['contentSources', responseBotId] });
      }

      // Call onSuccess callback
      queueMicrotask(() => {
        options?.onSuccess?.(successContent);
      });
    },
    onError: (error: ExtractGoogleErrorResponse, { url, contentId }) => {
      // Update content with error status
      setContents((prev) =>
        prev.map((c) => {
          if (c.id === contentId) {
            return {
              ...c,
              status: 'error' as GoogleDocStatus,
              errorMessage: error.error.message,
              errorCode: error.error.code,
            };
          }
          return c;
        })
      );

      // Call onError callback
      const failedContent = contents.find((c) => c.id === contentId);
      if (failedContent) {
        queueMicrotask(() => {
          options?.onError?.(failedContent, error);
        });
      }
    },
  });

  // Extract a Google document
  const extractGoogleDoc = useCallback(
    ({ url, botId }: { url: string; botId?: string }) => {
      const contentId = generateContentId();
      extractMutation.mutate({ url, contentId, ...(botId ? { botId } : {}) });
    },
    [extractMutation]
  );

  // Remove content from the list and delete from database
  const removeContent = useCallback(
    async (contentId: string) => {
      // Find the content to get its database contentId before removing
      const contentToRemove = contents.find((c) => c.id === contentId);

      setContents((prev) => prev.filter((c) => c.id !== contentId));

      // If content has a database contentId, delete from API
      if (contentToRemove?.contentId && createdBotId) {
        try {
          await fetch(`/api/bots/${createdBotId}/content/${contentToRemove.contentId}`, {
            method: 'DELETE',
          });
          // Invalidate content sources to refresh unified panel
          queryClient.invalidateQueries({ queryKey: ['contentSources', createdBotId] });
        } catch (error) {
          console.error('[useGoogleExtraction] Failed to delete content from API:', error);
        }
      }
    },
    [contents, createdBotId, queryClient]
  );

  // Remove content by its database contentId (for syncing with unified panel)
  const removeByContentId = useCallback((dbContentId: string) => {
    setContents((prev) => prev.filter((c) => c.contentId !== dbContentId));
  }, []);

  // Clear all contents and reset state
  const clearContents = useCallback(() => {
    setContents([]);
    setCreatedBotId(null);
  }, []);

  // Calculate total character count
  const totalCharacterCount = contents
    .filter((c) => c.status === 'ready')
    .reduce((acc, c) => acc + (c.characterCount || 0), 0);

  return {
    contents,
    isExtracting: extractMutation.isPending,
    extractGoogleDoc,
    removeContent,
    removeByContentId,
    clearContents,
    totalCharacterCount,
    botId: createdBotId,
  };
}

// Export types
export type { ExtractGoogleResponse, ExtractGoogleErrorResponse, GoogleExtractionErrorCode };
